import React, { useState, useCallback, useContext } from "react";
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useMediaQuery,
  useTheme,
  Tooltip,
  Alert,
} from "@mui/material";
import EditableGroup, {
  EditableGroupType,
  EditableGroupContext,
} from "../../editable/EditableGroup/EditableGroup";
import EditableField, {
  EditableFieldTypes,
} from "../../editable/EditableField/EditableField";
import { useTranslation } from "react-i18next";
import { mapRoleNameToDescription } from "../../../utility/utils";
import {
  PERMISSION_CATEGORIES_V2,
  getPermissionFlag,
  getParentPermission,
  type PermissionGroup,
  type Role,
} from "./types";
import { useRbacFlags, getFlagState } from "@/hooks/useRbacFlags";
import { validateRolesHavePermissions } from "./permissionHelpers";
import { ReleaseFlag, useReleaseManagement } from "@/utility/feature-management";

type PermissionMatrix = Record<string, Record<string, boolean>>;

interface ParentGatedSwitchCellProps {
  permission: string;
  roleName: string;
  parentPermission?: string;
  releasedChildPermissions: string[];
  dependentPermissions: string[];
  disabledTooltip: string;
  columnWidth: number;
}

const ParentGatedSwitchCell: React.FC<ParentGatedSwitchCellProps> = ({
  permission,
  roleName,
  parentPermission,
  releasedChildPermissions,
  dependentPermissions,
  disabledTooltip,
  columnWidth,
}) => {
  const { data, isEditing } = useContext(EditableGroupContext);
  const liveData = (data ?? {}) as PermissionMatrix;

  const isParentEnabledForRole =
    !parentPermission || liveData[parentPermission]?.[roleName] === true;
  const disabledByParent =
    parentPermission !== undefined &&
    permission !== parentPermission &&
    !isParentEnabledForRole;

  const onProvide = (d: PermissionMatrix) =>
    disabledByParent ? false : (d[permission]?.[roleName] ?? false);

  const onChange = (d: PermissionMatrix, value: boolean) => {
    if (!d[permission]) d[permission] = {};
    d[permission][roleName] = value;

    if (permission === parentPermission) {
      const cascadePermissions = value ? releasedChildPermissions : dependentPermissions;
      cascadePermissions.forEach((child) => {
        if (!d[child]) d[child] = {};
        d[child][roleName] = value;
      });
    }
  };

  if (!isEditing || !disabledByParent) {
    return <EditableField<PermissionMatrix, boolean>
      inputType={EditableFieldTypes.switch}
      label=""
      readOnly={disabledByParent}
      onProvide={onProvide}
      onChange={onChange}
      testId={`permission-${permission}-role-${roleName}`}
      columnWidth={columnWidth}
    />;
  }

  return (
    <Tooltip title={disabledTooltip} arrow>
      <Box
        aria-disabled
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          cursor: "not-allowed",
          "& > div": {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
          },
        }}
      >
        <EditableField<PermissionMatrix, boolean>
          inputType={EditableFieldTypes.switch}
          label=""
          readOnly={disabledByParent}
          onProvide={onProvide}
          onChange={onChange}
          testId={`permission-${permission}-role-${roleName}`}
          columnWidth={columnWidth}
        />
      </Box>
    </Tooltip>
  );
};

interface PermissionSectionV2Props {
  categoryKey: string;
  permissions: string[];
  roles: Role[];
  data: Record<string, Record<string, boolean>>;
  onDataChanged: (
    data: Record<string, Record<string, boolean>>,
  ) => Promise<boolean>;
}

export const PermissionSectionV2: React.FC<PermissionSectionV2Props> = ({
  categoryKey,
  permissions,
  roles,
  data,
  onDataChanged,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [validationError, setValidationError] = useState<string | null>(null);
  const rbacFlags = useRbacFlags();
  const isValidationEnabled = useReleaseManagement(ReleaseFlag.releaseRbacRoleCannotHaveZeroPermissions);

  const getParentPermissionRequiredMessage = (parentPermission: string) =>
    t("users.rolesAndPermissions.parentPermissionRequired", {
      permission: t(`users.rolesAndPermissions.permissions.${parentPermission}`),
    });

  const category = PERMISSION_CATEGORIES_V2[(categoryKey as (keyof typeof PERMISSION_CATEGORIES_V2))];

  const handleValidation = useCallback(async (
    data: Record<string, Record<string, boolean>>
  ): Promise<boolean> => {
    if (!isValidationEnabled) {
      return true;
    }

    // only validate the system access category for now
    if (categoryKey !== "systemAccess") {
      return true;
    }

    // filter data to only include system access permissions
    const systemAccessData: Record<string, Record<string, boolean>> = {};
    permissions.forEach(permission => {
      if (data[permission]) {
        systemAccessData[permission] = data[permission];
      }
    });

    // validate that all roles have at least 1 system access permission
    const validation = validateRolesHavePermissions(systemAccessData, roles);

    if (!validation.valid) {
      const roleNames = validation.rolesWithoutPermissions
        .map(name => mapRoleNameToDescription(name))
        .join(', ');

      setValidationError(
        t("users.rolesAndPermissions.validation.rolesMustHavePermissions", {
          roleName: roleNames
        })
      );
      return false;
    }

    setValidationError(null);
    return true;
  }, [roles, t, categoryKey, permissions, isValidationEnabled]);

  const handleDataChanged = useCallback(async (
    data: Record<string, Record<string, boolean>>
  ): Promise<boolean> => {
    try {
      const result = await onDataChanged(data);
      if (result) {
        setValidationError(null);
      }
      return result;
    } catch (error) {
      // error is handled by usePermissionsMatrix on rollback
      throw error;
    }
  }, [onDataChanged]);

  //clear validation error when canceling edit mode
  const handleCancelEdit = useCallback(() => {
    setValidationError(null);
  }, []);

  if (permissions.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 6 }}>
        <Typography variant="body2" color="text.secondary">
          {t("users.rolesAndPermissions.noPermissionsFound", {
            category:
              t(`users.rolesAndPermissions.cardTitles.${categoryKey}`) ||
              categoryKey,
          })}
        </Typography>
      </Box>
    );
  }

  const isSubCategoryFlagEnabled = (subCategory: PermissionGroup) => {
    const isEnabled = getFlagState(rbacFlags, subCategory.featureFlag);
    return isEnabled;
  }

  return (
    <>
      {validationError && (
        <Box sx={{ mb: 2 }}>
          <Alert data-testid="validation-error-alert" severity="error" onClose={() => setValidationError(null)}>
            {validationError}
          </Alert>
        </Box>
      )}
      <EditableGroup<Record<string, Record<string, boolean>>>
        id={`permissions-${categoryKey}`}
        editType={EditableGroupType.switchableEditing}
        data={data}
        onDataChanged={handleDataChanged}
        onValidate={isValidationEnabled && categoryKey === "systemAccess" ? handleValidation : undefined}
        onCancelEdit={handleCancelEdit}
        testId={`permissions-${categoryKey}-group`}
      >
        {category.subCategories.map((subCategory, index) => {
          const visiblePermissions = subCategory.permissions.filter(
            (permission) => getFlagState(rbacFlags, getPermissionFlag(subCategory, permission))
          );

          const childPermissionsByParent = new Map<string, string[]>();
          subCategory.permissions.forEach((permission) => {
            const parent = getParentPermission(subCategory, permission);
            if (parent) {
              childPermissionsByParent.set(parent, [
                ...(childPermissionsByParent.get(parent) ?? []),
                permission,
              ]);
            }
          });

          const getRowPermissionProps = (permission: string) => {
            const configuredParent = getParentPermission(subCategory, permission);
            const isParent = childPermissionsByParent.has(permission);
            
            const parentPermission = configuredParent ?? (isParent ? permission : undefined);
            const dependentPermissions = parentPermission
              ? childPermissionsByParent.get(parentPermission) ?? []
              : [];
            const releasedChildPermissions = dependentPermissions.filter((child) =>
              (visiblePermissions as readonly string[]).includes(child)
            );
            const disabledTooltip = parentPermission
              ? getParentPermissionRequiredMessage(parentPermission)
              : "";

            return { parentPermission, dependentPermissions, releasedChildPermissions, disabledTooltip };
          };

          return <React.Fragment key={`${categoryKey}-${subCategory.key || index}`}>
            {isMobile ? (
              isSubCategoryFlagEnabled(subCategory) && <Box sx={{ pb: visiblePermissions.length > 0 ? 4 : 0 }}>
                {subCategory.key && <Typography variant="body1" color="text.main" fontWeight="bold" sx={{ mb: 2 }}>
                  {t(`users.rolesAndPermissions.subHeadings.${subCategory.key}`)}
                </Typography>}
                {visiblePermissions.map(permission => {
                  const { parentPermission, dependentPermissions, releasedChildPermissions, disabledTooltip } =
                    getRowPermissionProps(permission);

                  return (
                    <Paper key={permission} sx={{ mb: 2, p: 2 }}>
                      <Typography fontWeight="bold" sx={{ mb: 1 }}>
                        {t(`users.rolesAndPermissions.permissions.${permission}`) || permission}
                      </Typography>
                      {roles.map(role => (
                        <Box key={role.name} display="flex" alignItems="center" mb={1}>
                          <Typography sx={{ minWidth: 200 }}>
                            {mapRoleNameToDescription(role.name)}
                          </Typography>
                          <ParentGatedSwitchCell
                            permission={permission}
                            roleName={role.name}
                            parentPermission={parentPermission}
                            releasedChildPermissions={releasedChildPermissions}
                            dependentPermissions={dependentPermissions}
                            disabledTooltip={disabledTooltip}
                            columnWidth={6}
                          />
                        </Box>
                      ))}
                    </Paper>
                  );
                })}
              </Box>
            ) : (
              isSubCategoryFlagEnabled(subCategory) && <Box sx={{ pb: visiblePermissions.length > 0 ? 4 : 0 }}>
                {subCategory.key && <Typography variant="body1" color="text.main" fontWeight="600" sx={{ mb: 2 }}>
                  {t(`users.rolesAndPermissions.subHeadings.${subCategory.key}`)}
                </Typography>}
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{
                    width: '100%',
                    overflow: 'auto',
                    scrollbarWidth: 'thin',
                    '&::-webkit-scrollbar': {
                      height: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: 'rgba(0,0,0,0.1)',
                      borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: 'rgba(0,0,0,0.3)',
                      borderRadius: '4px',
                      '&:hover': {
                        backgroundColor: 'rgba(0,0,0,0.5)',
                      },
                    },
                  }}
                >
                  <Table size="small" stickyHeader sx={{ minWidth: 800, tableLayout: 'fixed', borderCollapse: 'separate', borderSpacing: 0 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{
                          fontWeight: 'bold',
                          width: 160,
                          minWidth: 160,
                          maxWidth: 160,
                          position: 'sticky',
                          left: 0,
                          backgroundColor: 'background.paper',
                          borderRight: '1px solid',
                          borderColor: 'divider',
                          padding: { xs: '8px 4px', sm: '10px 6px', md: '12px 8px' },
                          fontSize: { xs: '0.75rem', sm: '0.85rem', md: '1rem' }
                        }}>
                          {t("users.permission")}
                        </TableCell>
                        {roles.map(role => (
                          <TableCell
                            key={role.name}
                            align="center"
                            sx={{
                              fontWeight: 'bold',
                              width: 90,
                              minWidth: 90,
                              maxWidth: 90,
                              zIndex: 1,
                              padding: { xs: '8px 2px', sm: '10px 3px', md: '12px 4px' }
                            }}
                          >
                            <Tooltip title={mapRoleNameToDescription(role.name)} arrow>
                              <Box sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                fontSize: '0.75rem'
                              }}>
                                {mapRoleNameToDescription(role.name)}
                              </Box>
                            </Tooltip>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visiblePermissions.map(permission => {
                        const fullPermissionText = t(`users.rolesAndPermissions.permissions.${permission}`) || permission;
                        const { parentPermission, dependentPermissions, releasedChildPermissions, disabledTooltip } =
                          getRowPermissionProps(permission);

                        return (
                          <TableRow key={permission} hover>
                            <TableCell
                              sx={{
                                fontWeight: 'medium',
                                width: 160,
                                minWidth: 160,
                                maxWidth: 160,
                                position: 'sticky',
                                left: 0,
                                backgroundColor: 'background.paper',
                                zIndex: 1,
                                borderRight: '1px solid',
                                borderColor: 'divider',
                                padding: { xs: '8px 4px', sm: '10px 6px', md: '12px 8px' },
                                fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' }
                              }}
                            >
                              <Tooltip title={fullPermissionText} arrow placement="top-start">
                                <Box sx={{
                                  whiteSpace: 'normal',
                                  wordBreak: 'break-word',
                                }}>
                                  {fullPermissionText}
                                </Box>
                              </Tooltip>
                            </TableCell>
                            {roles.map(role => (
                              <TableCell
                                key={`${permission}-${role.name}`}
                                align="center"
                                sx={{
                                  textAlign: 'center',
                                  padding: { xs: '6px 2px', sm: '8px 3px', md: '8px 4px' },
                                  width: 90,
                                  minWidth: 90,
                                  maxWidth: 90,
                                  '& > div': {
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    width: '100%'
                                  }
                                }}
                              >
                                <ParentGatedSwitchCell
                                  permission={permission}
                                  roleName={role.name}
                                  parentPermission={parentPermission}
                                  releasedChildPermissions={releasedChildPermissions}
                                  dependentPermissions={dependentPermissions}
                                  disabledTooltip={disabledTooltip}
                                  columnWidth={12}
                                />
                              </TableCell>
                            ))}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </React.Fragment>
        })}
      </EditableGroup>
    </>
  );
};
